import { type CSSProperties } from 'react';
import {
  FAMILY_ALLERGY_SEVERITIES,
  FAMILY_ALLERGY_TYPES,
  FAMILY_MEDICATION_STATUSES,
  FAMILY_RELATIONSHIP_TYPES,
  PATIENT_SEX_OPTIONS,
  memberHasHistory,
  type FamilyHistoryInput,
  type FamilyHistoryStatus,
  type FamilyMemberInput,
  type FamilyValidationError,
} from '@helios/domain';

/**
 * PRD.267 — standardized (non-configurable in V1) editor for the Family
 * Allergies & Medications step. Family records are clearly labeled as
 * family-history context — they never become the patient's own allergies or
 * medications.
 */

export const FAMILY_HELPER_TEXT =
  'Record allergies and medications of family members that may help clinical assessment. These do not become the patient’s own allergies or medications.';

const STATUS_OPTIONS: { value: FamilyHistoryStatus; label: string }[] = [
  { value: 'NOT_ASKED', label: 'Not asked' },
  { value: 'NONE_KNOWN', label: 'No known family allergies or medications' },
  { value: 'UNKNOWN', label: 'Patient does not know' },
  { value: 'HAS_HISTORY', label: 'Has family history (add members below)' },
];

function titleCase(v: string): string {
  return v
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

const labelStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-medium)',
  color: 'var(--text-muted)',
  marginBottom: 'var(--sp-1)',
  display: 'block',
};

const inputStyle: CSSProperties = {
  width: '100%',
  height: 40,
  padding: '0 var(--sp-3)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  color: 'var(--text)',
  fontSize: 'var(--font-size-base)',
};

const errorInputStyle: CSSProperties = { ...inputStyle, border: '1px solid var(--color-danger)' };

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ flex: 1, minWidth: 160 }}>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );
}

export function FamilyHistoryEditor({
  value,
  onChange,
  showErrors = false,
  errors = [],
}: {
  value: FamilyHistoryInput;
  onChange: (next: FamilyHistoryInput) => void;
  showErrors?: boolean;
  errors?: FamilyValidationError[];
}) {
  const hasError = (path: string) => showErrors && errors.some((e) => e.path === path);

  const setStatus = (status: FamilyHistoryStatus) => onChange({ ...value, historyStatus: status });

  const updateMembers = (members: FamilyMemberInput[]) => onChange({ ...value, members });

  const addMember = () =>
    updateMembers([
      ...value.members,
      { relationshipType: '', relationshipLabel: '', allergies: [], medications: [] },
    ]);

  const patchMember = (i: number, patch: Partial<FamilyMemberInput>) =>
    updateMembers(value.members.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));

  const removeMember = (i: number) => updateMembers(value.members.filter((_, idx) => idx !== i));

  const showMembers = value.historyStatus !== 'NONE_KNOWN' && value.historyStatus !== 'UNKNOWN';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
      <div>
        <h3
          style={{
            fontSize: 'var(--font-size-lg)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text)',
            margin: 0,
          }}
        >
          Family Allergies &amp; Medications
        </h3>
        <p
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--text-muted)',
            margin: 'var(--sp-1) 0 0',
          }}
        >
          {FAMILY_HELPER_TEXT}
        </p>
      </div>

      <Field label="Family history status">
        <select
          value={value.historyStatus}
          onChange={(e) => setStatus(e.target.value as FamilyHistoryStatus)}
          style={inputStyle}
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </Field>

      {hasError('family.history') && (
        <span style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)' }}>
          {errors.find((e) => e.path === 'family.history')?.message}
        </span>
      )}

      {showMembers && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
          {value.members.map((member, i) => (
            <MemberCard
              key={i}
              index={i}
              member={member}
              onPatch={(patch) => patchMember(i, patch)}
              onRemove={() => removeMember(i)}
              hasError={hasError}
            />
          ))}

          <button
            type="button"
            onClick={addMember}
            style={{
              alignSelf: 'flex-start',
              height: 40,
              padding: '0 var(--sp-4)',
              borderRadius: 'var(--radius-md)',
              border: '1px dashed var(--border-strong)',
              background: 'transparent',
              color: 'var(--color-primary)',
              fontSize: 'var(--font-size-base)',
              fontWeight: 'var(--font-weight-medium)',
              cursor: 'pointer',
            }}
          >
            + Add family member
          </button>
        </div>
      )}
    </div>
  );
}

function MemberCard({
  index,
  member,
  onPatch,
  onRemove,
  hasError,
}: {
  index: number;
  member: FamilyMemberInput;
  onPatch: (patch: Partial<FamilyMemberInput>) => void;
  onRemove: () => void;
  hasError: (path: string) => boolean;
}) {
  const base = `members[${index}]`;

  const addAllergy = () => onPatch({ allergies: [...member.allergies, { substance: '' }] });
  const patchAllergy = (i: number, patch: Partial<(typeof member.allergies)[number]>) =>
    onPatch({ allergies: member.allergies.map((a, idx) => (idx === i ? { ...a, ...patch } : a)) });
  const removeAllergy = (i: number) =>
    onPatch({ allergies: member.allergies.filter((_, idx) => idx !== i) });

  const addMed = () => onPatch({ medications: [...member.medications, { name: '' }] });
  const patchMed = (i: number, patch: Partial<(typeof member.medications)[number]>) =>
    onPatch({
      medications: member.medications.map((m, idx) => (idx === i ? { ...m, ...patch } : m)),
    });
  const removeMed = (i: number) =>
    onPatch({ medications: member.medications.filter((_, idx) => idx !== i) });

  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--sp-4)',
        background: 'var(--bg-surface)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-3)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 'var(--font-weight-semi)', color: 'var(--text)' }}>
          Family member {index + 1}
        </span>
        <button
          type="button"
          onClick={onRemove}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--color-danger)',
            cursor: 'pointer',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          Remove
        </button>
      </div>

      <div style={{ display: 'flex', gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
        <Field label="Relationship *">
          <select
            value={member.relationshipType}
            onChange={(e) => onPatch({ relationshipType: e.target.value })}
            style={hasError(`${base}.relationshipType`) ? errorInputStyle : inputStyle}
          >
            <option value="">Select relationship…</option>
            {FAMILY_RELATIONSHIP_TYPES.map((r) => (
              <option key={r} value={r}>
                {titleCase(r)}
              </option>
            ))}
          </select>
        </Field>
        {member.relationshipType === 'OTHER' && (
          <Field label="Relationship label *">
            <input
              value={member.relationshipLabel ?? ''}
              onChange={(e) => onPatch({ relationshipLabel: e.target.value })}
              style={hasError(`${base}.relationshipLabel`) ? errorInputStyle : inputStyle}
              placeholder="e.g. Aunt"
            />
          </Field>
        )}
        <Field label="Name">
          <input
            value={member.name ?? ''}
            onChange={(e) => onPatch({ name: e.target.value })}
            style={inputStyle}
          />
        </Field>
      </div>

      <div style={{ display: 'flex', gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
        <Field label="Sex">
          <select
            value={member.sex ?? ''}
            onChange={(e) => onPatch({ sex: e.target.value })}
            style={inputStyle}
          >
            <option value="">—</option>
            {PATIENT_SEX_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Birthdate">
          <input
            type="date"
            value={member.birthDate ?? ''}
            onChange={(e) => onPatch({ birthDate: e.target.value })}
            style={inputStyle}
          />
        </Field>
        <Field label="Age">
          <input
            value={member.ageText ?? ''}
            onChange={(e) => onPatch({ ageText: e.target.value })}
            style={inputStyle}
          />
        </Field>
      </div>

      <Field label="Notes">
        <input
          value={member.notes ?? ''}
          onChange={(e) => onPatch({ notes: e.target.value })}
          style={inputStyle}
        />
      </Field>

      {/* Allergies */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
        <span style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--text)' }}>
          Allergies
        </span>
        {member.allergies.map((a, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              gap: 'var(--sp-2)',
              flexWrap: 'wrap',
              alignItems: 'flex-end',
            }}
          >
            <Field label="Substance *">
              <input
                value={a.substance}
                onChange={(e) => patchAllergy(i, { substance: e.target.value })}
                style={hasError(`${base}.allergies[${i}].substance`) ? errorInputStyle : inputStyle}
              />
            </Field>
            <Field label="Type">
              <select
                value={a.allergyType ?? ''}
                onChange={(e) => patchAllergy(i, { allergyType: e.target.value })}
                style={inputStyle}
              >
                <option value="">—</option>
                {FAMILY_ALLERGY_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {titleCase(t)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Reaction">
              <input
                value={a.reaction ?? ''}
                onChange={(e) => patchAllergy(i, { reaction: e.target.value })}
                style={inputStyle}
              />
            </Field>
            <Field label="Severity">
              <select
                value={a.severity ?? ''}
                onChange={(e) => patchAllergy(i, { severity: e.target.value })}
                style={inputStyle}
              >
                <option value="">—</option>
                {FAMILY_ALLERGY_SEVERITIES.map((s) => (
                  <option key={s} value={s}>
                    {titleCase(s)}
                  </option>
                ))}
              </select>
            </Field>
            <button
              type="button"
              onClick={() => removeAllergy(i)}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--color-danger)',
                cursor: 'pointer',
                height: 40,
              }}
            >
              ✕
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addAllergy}
          style={{
            alignSelf: 'flex-start',
            background: 'transparent',
            border: 'none',
            color: 'var(--color-primary)',
            cursor: 'pointer',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          + Add allergy
        </button>
      </div>

      {/* Medications */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
        <span style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--text)' }}>
          Medications
        </span>
        {member.medications.map((m, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              gap: 'var(--sp-2)',
              flexWrap: 'wrap',
              alignItems: 'flex-end',
            }}
          >
            <Field label="Medication name *">
              <input
                value={m.name}
                onChange={(e) => patchMed(i, { name: e.target.value })}
                style={hasError(`${base}.medications[${i}].name`) ? errorInputStyle : inputStyle}
              />
            </Field>
            <Field label="Dose">
              <input
                value={m.dose ?? ''}
                onChange={(e) => patchMed(i, { dose: e.target.value })}
                style={inputStyle}
              />
            </Field>
            <Field label="Frequency">
              <input
                value={m.frequency ?? ''}
                onChange={(e) => patchMed(i, { frequency: e.target.value })}
                style={inputStyle}
              />
            </Field>
            <Field label="Status">
              <select
                value={m.status ?? ''}
                onChange={(e) => patchMed(i, { status: e.target.value })}
                style={inputStyle}
              >
                <option value="">—</option>
                {FAMILY_MEDICATION_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {titleCase(s)}
                  </option>
                ))}
              </select>
            </Field>
            <button
              type="button"
              onClick={() => removeMed(i)}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--color-danger)',
                cursor: 'pointer',
                height: 40,
              }}
            >
              ✕
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addMed}
          style={{
            alignSelf: 'flex-start',
            background: 'transparent',
            border: 'none',
            color: 'var(--color-primary)',
            cursor: 'pointer',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          + Add medication
        </button>
      </div>

      {!memberHasHistory(member) && (
        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
          Add at least one allergy or medication for this member, or remove the card.
        </span>
      )}
    </div>
  );
}
